import { ProductTypes } from "services/KnowfullyAdminClient";
import { SkuAvailabilityId } from "types/common-types";
import { SkuFormValues } from "types/sku-related-types";

export type SkuFieldKey =
  | "ownerOrganizationId"
  | "productId"
  | "corporateEntityId"
  | "skuFormatId"
  | "skuAvailabilityId"
  | "skuStatusId"
  | "sku"
  | "productYearId"
  | "productVersionId"
  | "title"
  | "description"
  | "sequence"
  | "advancePreparationId"
  | "releaseDate"
  | "expirationDate"
  | "offlineDate"
  | "credits"
  | "creditLimit"
  | "creditSubmissionExpirationDate"
  | "eventStartDate"
  | "eventEndDate"
  | "price"
  | "memberPrice"
  | "hasChild"
  | "evaluationsSection"
  | "certificatesSection"
  | "skuRules"
  | "skuRelationships"
  | "skuLinks"
  | "courseProgressionLogic"
  | "knowledgeLevels"
  | "learningLevels"
  | "faculty"
  | "studyFields"
  | "majorTopics"
  | "learningObjectives"
  | "qualifications"
  | "sponsors"
  | "courseHandouts"
  | "contentDelivery";

export type SkuFieldRule = {
  visible: boolean;
  required: boolean;
  autoPopulated?: boolean;
};

type FieldRules = Record<SkuFieldKey, SkuFieldRule>;

const V: SkuFieldRule = { visible: true, required: false };
const VR: SkuFieldRule = { visible: true, required: true };
const H: SkuFieldRule = { visible: false, required: false };
const HA: SkuFieldRule = { visible: false, required: false, autoPopulated: true };

const catalogRules: FieldRules = {
  ownerOrganizationId: VR,
  productId: V,
  corporateEntityId: V,
  skuFormatId: HA,
  skuAvailabilityId: HA,
  skuStatusId: VR,
  sku: VR,
  productYearId: H,
  productVersionId: H,
  title: VR,
  description: V,
  sequence: H,
  advancePreparationId: H,
  releaseDate: H,
  expirationDate: H,
  offlineDate: H,
  credits: H,
  creditLimit: H,
  creditSubmissionExpirationDate: H,
  eventStartDate: H,
  eventEndDate: H,
  price: H,
  memberPrice: H,
  hasChild: V,
  evaluationsSection: H,
  certificatesSection: H,
  skuRules: V,
  skuRelationships: V,
  skuLinks: H,
  courseProgressionLogic: H,
  knowledgeLevels: H,
  learningLevels: H,
  faculty: H,
  studyFields: H,
  majorTopics: H,
  learningObjectives: H,
  qualifications: H,
  sponsors: H,
  courseHandouts: H,
  contentDelivery: H,
};

const membershipRules: FieldRules = {
  ownerOrganizationId: VR,
  productId: V,
  corporateEntityId: V,
  skuFormatId: HA,
  skuAvailabilityId: HA,
  skuStatusId: VR,
  sku: VR,
  productYearId: H,
  productVersionId: H,
  title: VR,
  description: V,
  sequence: H,
  advancePreparationId: H,
  releaseDate: V,
  expirationDate: V,
  offlineDate: V,
  credits: V,
  creditLimit: V,
  creditSubmissionExpirationDate: H,
  eventStartDate: H,
  eventEndDate: H,
  price: V,
  memberPrice: V,
  hasChild: V,
  evaluationsSection: H,
  certificatesSection: H,
  skuRules: V,
  skuRelationships: V,
  skuLinks: H,
  courseProgressionLogic: H,
  knowledgeLevels: H,
  learningLevels: H,
  faculty: H,
  studyFields: H,
  majorTopics: H,
  learningObjectives: H,
  qualifications: H,
  sponsors: H,
  courseHandouts: H,
  contentDelivery: H,
};

const courseProductRules: FieldRules = {
  ownerOrganizationId: VR,
  productId: V,
  corporateEntityId: V,
  skuFormatId: V,
  skuAvailabilityId: VR,
  skuStatusId: VR,
  sku: VR,
  productYearId: V,
  productVersionId: V,
  title: VR,
  description: V,
  sequence: V,
  advancePreparationId: V,
  releaseDate: V,
  expirationDate: V,
  offlineDate: V,
  credits: V,
  creditLimit: H,
  creditSubmissionExpirationDate: V,
  eventStartDate: V,
  eventEndDate: V,
  price: V,
  memberPrice: V,
  hasChild: H,
  evaluationsSection: V,
  certificatesSection: V,
  skuRules: H,
  skuRelationships: V,
  skuLinks: V,
  courseProgressionLogic: V,
  knowledgeLevels: V,
  learningLevels: V,
  faculty: V,
  studyFields: V,
  majorTopics: V,
  learningObjectives: V,
  qualifications: V,
  sponsors: V,
  courseHandouts: V,
  contentDelivery: V,
};

const seriesRules: FieldRules = {
  ownerOrganizationId: VR,
  productId: V,
  corporateEntityId: H,
  skuFormatId: HA,
  skuAvailabilityId: HA,
  skuStatusId: VR,
  sku: VR,
  productYearId: V,
  productVersionId: V,
  title: VR,
  description: V,
  sequence: V,
  advancePreparationId: H,
  releaseDate: V,
  expirationDate: V,
  offlineDate: V,
  credits: V,
  creditLimit: H,
  creditSubmissionExpirationDate: V,
  eventStartDate: H,
  eventEndDate: H,
  price: V,
  memberPrice: V,
  hasChild: V,
  evaluationsSection: V,
  certificatesSection: V,
  skuRules: V,
  skuRelationships: V,
  skuLinks: V,
  courseProgressionLogic: H,
  knowledgeLevels: H,
  learningLevels: H,
  faculty: H,
  studyFields: H,
  majorTopics: H,
  learningObjectives: H,
  qualifications: V,
  sponsors: V,
  courseHandouts: H,
  contentDelivery: H,
};

const hiddenRules: FieldRules = {
  ownerOrganizationId: H,
  productId: H,
  corporateEntityId: H,
  skuFormatId: H,
  skuAvailabilityId: H,
  skuStatusId: H,
  sku: H,
  productYearId: H,
  productVersionId: H,
  title: H,
  description: H,
  sequence: H,
  advancePreparationId: H,
  releaseDate: H,
  expirationDate: H,
  offlineDate: H,
  credits: H,
  creditLimit: H,
  creditSubmissionExpirationDate: H,
  eventStartDate: H,
  eventEndDate: H,
  price: H,
  memberPrice: H,
  hasChild: H,
  evaluationsSection: H,
  certificatesSection: H,
  skuRules: H,
  skuRelationships: H,
  skuLinks: H,
  courseProgressionLogic: H,
  knowledgeLevels: H,
  learningLevels: H,
  faculty: H,
  studyFields: H,
  majorTopics: H,
  learningObjectives: H,
  qualifications: H,
  sponsors: H,
  courseHandouts: H,
  contentDelivery: H,
};

export const skuFieldVisibilityMap: Record<ProductTypes, FieldRules> = {
  [ProductTypes.Catalog]: catalogRules,
  [ProductTypes.Membership]: membershipRules,
  [ProductTypes.Package]: membershipRules,
  [ProductTypes.SuperPackage]: membershipRules,
  [ProductTypes.ResourceLibrary]: membershipRules,
  [ProductTypes.Product]: courseProductRules,
  [ProductTypes.ThirdParty]: courseProductRules,
  [ProductTypes.Series]: seriesRules,
  [ProductTypes.Unknown]: hiddenRules,
};

export const getFieldRule = (
  productTypeEnumValue: ProductTypes | undefined,
  fieldKey: SkuFieldKey
): SkuFieldRule => {
  if (productTypeEnumValue === undefined) {
    return H;
  }
  const ruleSet = skuFieldVisibilityMap[productTypeEnumValue] ?? hiddenRules;
  return ruleSet[fieldKey] ?? H;
};

export type SkuFormatDefaultName = "Catalog" | "Membership" | "Series";

export const targetSkuFormatName = (
  productTypeEnumValue: ProductTypes | undefined
): SkuFormatDefaultName | undefined => {
  if (productTypeEnumValue === undefined) {
    return undefined;
  }
  switch (productTypeEnumValue) {
    case ProductTypes.Catalog:
      return "Catalog";
    case ProductTypes.Membership:
    case ProductTypes.Package:
    case ProductTypes.SuperPackage:
    case ProductTypes.ResourceLibrary:
      return "Membership";
    case ProductTypes.Series:
      return "Series";
    default:
      return undefined;
  }
};

export const getDefaultsForProductType = (
  productTypeEnumValue: ProductTypes | undefined
): Partial<SkuFormValues> => {
  if (productTypeEnumValue === undefined) {
    return {};
  }
  switch (productTypeEnumValue) {
    case ProductTypes.Catalog:
    case ProductTypes.Membership:
    case ProductTypes.Package:
    case ProductTypes.SuperPackage:
    case ProductTypes.ResourceLibrary:
    case ProductTypes.Series:
      return { skuAvailabilityId: SkuAvailabilityId.OnDemand };
    case ProductTypes.Product:
    case ProductTypes.ThirdParty:
    case ProductTypes.Unknown:
      return {};
  }
};

export const isFieldVisible = (
  productTypeEnumValue: ProductTypes | undefined,
  fieldKey: SkuFieldKey,
  values: Pick<SkuFormValues, "skuAvailabilityId">
): boolean => {
  const rule = getFieldRule(productTypeEnumValue, fieldKey);
  if (!rule.visible) {
    return false;
  }
  if (fieldKey === "eventStartDate" || fieldKey === "eventEndDate") {
    return (
      values.skuAvailabilityId?.toLowerCase() ===
      SkuAvailabilityId.Live.toLowerCase()
    );
  }
  return true;
};

const ALL_FIELD_KEYS = Object.keys(catalogRules) as SkuFieldKey[];

export const assignSkuField = (
  target: SkuFormValues,
  key: SkuFieldKey,
  value: unknown
): void => {
  (target as Record<string, unknown>)[key] = value;
};

export const readSkuField = (
  source: SkuFormValues,
  key: SkuFieldKey
): unknown => (source as Record<string, unknown>)[key];

export const isEmptyFormValue = (value: unknown): boolean =>
  value == null || value === "";

export const stripHiddenFields = (
  productTypeEnumValue: ProductTypes | undefined,
  values: SkuFormValues
): SkuFormValues => {
  if (
    productTypeEnumValue === undefined ||
    productTypeEnumValue === ProductTypes.Unknown
  ) {
    return values;
  }
  const payload = { ...values };
  for (const key of ALL_FIELD_KEYS) {
    const visible = isFieldVisible(productTypeEnumValue, key, {
      skuAvailabilityId: values.skuAvailabilityId,
    });
    if (visible) {
      continue;
    }
    const rule = getFieldRule(productTypeEnumValue, key);
    if (rule.autoPopulated) {
      continue;
    }
    assignSkuField(payload, key, undefined);
  }
  return payload;
};

export type AutoPopulateApply = <K extends keyof SkuFormValues>(
  key: K,
  value: NonNullable<SkuFormValues[K]>
) => void;

export const applyAutoPopulateDefaults = (
  productTypeEnumValue: ProductTypes | undefined,
  skuFormats: { id?: string; name?: string }[],
  apply: AutoPopulateApply
): void => {
  if (productTypeEnumValue === undefined) {
    return;
  }
  const availabilityDefault = getDefaultsForProductType(productTypeEnumValue)
    .skuAvailabilityId;
  if (availabilityDefault !== undefined) {
    apply("skuAvailabilityId", availabilityDefault);
  }
  const formatName = targetSkuFormatName(productTypeEnumValue);
  if (formatName) {
    const match = skuFormats.find((f) => f.name === formatName);
    if (match?.id) {
      apply("skuFormatId", match.id);
    }
  }
};
